import { DialogDescription } from '@radix-ui/react-dialog'
import type { JSX, PropsWithChildren } from 'react'
import { LoadingPageIcon } from '@/assets/icons/loading-page-icon'
import { Show } from '@/components/show'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { Button } from '../../ui/button'

export interface ModalProps extends PropsWithChildren {
  isOpen: boolean
  setOpen: (value: boolean) => void
  title: string | JSX.Element
  hiddenFooter?: boolean
  className?: string
  backButtonLabel?: string
  backButtonAction?: () => void
  backButtonLabelClassName?: string
  submitButtonLabel?: string
  submitButtonclassName?: string
  submitButtonDisabled?: boolean
  pendingSubmit?: boolean
  submitAction?: () => void
  trigger?: { className?: string; label?: string }
  description?: string
  isLoading?: boolean
}

export const Modal = ({
  isOpen,
  setOpen,
  title,
  children,
  className,
  backButtonLabel,
  backButtonLabelClassName,
  submitAction,
  pendingSubmit,
  submitButtonLabel,
  submitButtonclassName,
  submitButtonDisabled,
  description,
  hiddenFooter,
  backButtonAction,
  trigger,
  isLoading
}: ModalProps) => {
  const handleSubmit = () => {
    if (submitAction) return submitAction()
  }

  const handleBack = () => {
    setOpen(false)
    if (backButtonAction) backButtonAction()
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          <Button className={trigger.className}>{trigger.label}</Button>
        </DialogTrigger>
      )}
      <DialogContent className={cn('p-0 border-none', className)}>
        <DialogHeader className="flex items-start justify-center rounded-t-md bg-[var(--brand-blue-2)] min-h-12">
          <DialogTitle className="px-5 py-3 text-white text-base">
            {title}
          </DialogTitle>
        </DialogHeader>
        {isLoading && (
          <div className="w-full h-full min-h-20 py-5 flex items-center justify-center">
            <LoadingPageIcon />
          </div>
        )}
        {!isLoading && (
          <>
            <DialogDescription className="sr-only">
              {description}
            </DialogDescription>
            <div className="px-6">{children}</div>
            <div className="border-b border-muted-foreground/60 mx-6"></div>
            <Show when={!hiddenFooter}>
              <DialogFooter className="px-6 pb-4">
                <div className="flex w-full justify-between">
                  <div className="min-w-44">
                    <Button
                      variant="light"
                      className={backButtonLabelClassName}
                      onClick={handleBack}
                      type="button"
                    >
                      {backButtonLabel}
                    </Button>
                  </div>

                  <div className="min-w-44">
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      className={submitButtonclassName}
                      disabled={submitButtonDisabled}
                      isLoading={pendingSubmit}
                    >
                      {submitButtonLabel}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </Show>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
